import React, { useEffect, useState } from "react";

// --- CSS --- //
import "../../styles/gym-certify.css";

// --- COMPONENTS --- //
import SearchBar from "../../components/SearchBar";
import Card from "../../components/Cards/CertificationCard.jsx";

// --- ASSETS --- //
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// --- FIREBASE --- //
import { getAdminGym, getCertificationIDList, getCertificationsDetails, getUserProfile } from "../../utilities/firestore/firestoreUtils.js";
import { getUserCertifications } from "../../utilities/firestore/firestoreUtils.js";

// --- UTILS --- //
import { formatDate, formatTime, calculateDaysUntilExpiration, formatLongDateTime } from "../../utilities/timeUtils.js";
import { EmployeeVerificationPopup } from "../../utilities/popupUtils.js";

const GymCertify = () => {
    const [isFocused, setIsFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isEmployeeVerificationPopupOpen, setIsEmployeeVerificationPopupOpen] = useState(false);
    const [gymName, setGymName] = useState(null);
    const [user, setUser] = useState(null);
    const [certifications, setCertifications] = useState([]);
    const [userCertifications, setUserCertifications] = useState([]);
    const [filteredCertifications, setFilteredCertifications] = useState([]);
    const [selectedCertifications, setSelectedCertifications] = useState([]); // Track selected certifications
    const [currentDateTime, setCurrentDateTime] = useState(new Date()); // Add state for date and time

    // Update current date and time every second
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 1000);

        // Cleanup interval on component unmount
        return () => clearInterval(interval);
    }, []);

    // Focus the search bar on component mount
    useEffect(() => {
        setIsFocused(true);

        const fetchGym = async () => {
            try {
                const gymName = await getAdminGym();
                setGymName(gymName);
            } catch (err) {
            }
        };

        fetchGym();
    }, []);

    // Fetch all certification IDs and details on page load
    useEffect(() => {
        const fetchCertifications = async () => {
            try {
                const certIDList = await getCertificationIDList();

                if (certIDList.length > 0) {
                    const certDetails = await getCertificationsDetails(certIDList);
                    setCertifications(certDetails);
                }
            } catch (error) {
            }
        };

        fetchCertifications();
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            if (searchQuery.trim() !== "") {
                try {
                    const certs = await getUserCertifications(searchQuery.trim());
                    const user = await getUserProfile(searchQuery);
                    setUserCertifications(certs);
                    setUser(user); // Only set user after it's fetched
    
                    // Remove selected certifications if the user already has them
                    setSelectedCertifications((prevSelected) => {
                        const userCertIDs = certs.map(cert => cert.id);
                        return prevSelected.filter(cert => !userCertIDs.includes(cert.id)); // Keep only certifications not owned by user
                    });
                } catch (error) {
                }
            }
        };
    
        fetchUserData();
    }, [searchQuery]);

    // Filter out certifications that the user already has
    useEffect(() => {
        const filterCertifications = () => {
            const userCertIDs = userCertifications.map(cert => cert.id);
            const filtered = certifications.filter(cert => !userCertIDs.includes(cert.id));
            setFilteredCertifications(filtered);
        };

        if (certifications.length > 0) {
            filterCertifications();
        }
    }, [certifications, userCertifications]);

    // Handle search input changes
    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    // Toggle certification selection
    const handleCertificationSelection = (certId, certName) => {
        setSelectedCertifications((prevSelected) => {
            const isAlreadySelected = prevSelected.some(cert => cert.id === certId);

            if (isAlreadySelected) {
                // Deselect the certification
                return prevSelected.filter(cert => cert.id !== certId);
            } else {
                // Select the certification
                return [...prevSelected, { id: certId, name: certName }];
            }
        });
    };

    const closeEmployeeVerificationPopup = (setIsEmployeeVerificationPopupOpen) => {
        setIsEmployeeVerificationPopupOpen(false);
    
        // Reset states to simulate a page refresh
        setSearchQuery(""); // Clear the search bar
        setUser(null); // Clear user data
        setUserCertifications([]); // Clear user's certifications
        setFilteredCertifications([]); // Clear filtered certifications
        setSelectedCertifications([]); // Clear selected certifications
    };

    // Optionally, display the count of selected certifications
    const selectedCount = selectedCertifications.length;

    return (
        <div className="certify-container">
            <div className="certify-search-container">
                <div className="certify-search-bar-container">
                    <SearchBar
                        isFocused={isFocused}
                        setIsFocused={setIsFocused}
                        searchQuery={searchQuery}
                        setSearchQuery={handleSearchChange}
                        placeholder={"Scan or Enter Vert Cert ID..."}
                    />
                </div>
                {userCertifications.map((cert, index) => (
                    <Card
                        key={cert.id} // Use a unique key for the map
                        certTitle={cert.title}
                        certGym={cert.gym}
                        certEmployee={cert.employee}
                        startDate={formatDate(cert.startDate)}
                        endDate={formatDate(cert.endDate)}
                        startTime={formatTime(cert.startDate)}
                        endTime={formatTime(cert.endDate)}
                        certType={cert.type}
                        dateDifference={calculateDaysUntilExpiration(cert.endDate)}
                    />
                ))}
            </div>
            <div className="certify-content-container">
                <div className="certify-content-header">
                    <div>
                        <h4 className="certify-title">{gymName ?? ""}</h4>
                        <p className="certify-date">{formatLongDateTime(currentDateTime)}</p>
                    </div>
                    {user !== null  && selectedCertifications.length > 0 ?
                        <button className="certify-button" onClick={() => setIsEmployeeVerificationPopupOpen(true)}>
                            Certify {user.firstName ?? ""}
                        </button> : <></>}
                </div>
                <div className="certify-content-body">
                    <h3 className="certify-content-title">Select Completed Certifications</h3>
                    <p className="certify-selected">
                        {selectedCount > 1
                            ? `${selectedCertifications[0].name} + ${selectedCount - 1} Selected`
                            : selectedCertifications.length > 0
                                ? selectedCertifications.map(cert => cert.name).join(", ") + " Selected"
                                : ""}
                    </p>
                    <div className="certify-cert-content">
                        {filteredCertifications.length > 0 ? (
                            filteredCertifications.map((cert) => (
                                <div
                                    key={cert.id}
                                    className="certify-content"
                                    onClick={() => handleCertificationSelection(cert.id, cert.name)}
                                >
                                    {selectedCertifications.some(selected => selected.id === cert.id) ? (
                                        <CheckBoxIcon className="certify-checkbox" />
                                    ) : (
                                        <CheckBoxOutlineBlankIcon className="certify-checkbox" />
                                    )}
                                    <div className="certify-text-content">
                                        <h4 className="certify-name">{cert.name}</h4>
                                        <p className="certify-id">{cert.id}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{color: "var(--text-primary)"}}>No new certifications available</p>
                        )}
                    </div>
                </div>
            </div>
            <EmployeeVerificationPopup
                isEmployeeVerificationOpen={isEmployeeVerificationPopupOpen}
                onEmployeeVerificationClose={() => closeEmployeeVerificationPopup(setIsEmployeeVerificationPopupOpen)}
                certificationData={selectedCertifications}
                certUserID={searchQuery}
            />
        </div>
    );
};

export default GymCertify;